/**
 * Preview arithmetic for partial delivery. Keep in sync with
 * pilot-server/utils/partialDelivery.js — same paise rules, same remainder on child 1.
 */

function toPaise(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

function fromPaise(paise) {
  return paise / 100;
}

function lineUnitPaise(product) {
  return Math.round(Number(product?.price || 0) * 100);
}

function lineValuePaise(product) {
  const qty = Number(product?.quantity) || 0;
  return lineUnitPaise(product) * qty;
}

function cloneLine(product, quantity) {
  const cloned = {
    ...product,
    quantity,
  };
  if (product?.variant && typeof product.variant === 'object') {
    cloned.variant = { ...product.variant };
  }
  if (product?.customizations && typeof product.customizations === 'object') {
    cloned.customizations = { ...product.customizations };
  }
  return cloned;
}

function partitionProducts(products, unavailableItems) {
  const byIndex = new Map();
  (unavailableItems || []).forEach((item) => {
    byIndex.set(Number(item.index), item);
  });

  const availableProducts = [];
  const unavailableProducts = [];

  (products || []).forEach((product, index) => {
    const lineQty = Number(product.quantity) || 0;
    const item = byIndex.get(index);
    if (!item) {
      if (lineQty > 0) availableProducts.push(cloneLine(product, lineQty));
      return;
    }
    const unavailQty = Number(item.quantity);
    const availQty = lineQty - unavailQty;
    if (availQty > 0) availableProducts.push(cloneLine(product, availQty));
    if (unavailQty > 0) unavailableProducts.push(cloneLine(product, unavailQty));
  });

  return { availableProducts, unavailableProducts };
}

function prorateMoney(parent, availableProducts, unavailableProducts, action) {
  const parentTotalPaise = toPaise(parent.total);
  const parentDiscountPaise = toPaise(parent.discount);
  const parentShippingPaise = toPaise(parent.shippingCost);

  const orderSubtotalPaise = (parent.products || []).reduce(
    (sum, product) => sum + lineValuePaise(product),
    0
  );
  const availSubtotalPaise = (availableProducts || []).reduce(
    (sum, product) => sum + lineValuePaise(product),
    0
  );
  const unavailSubtotalPaise = (unavailableProducts || []).reduce(
    (sum, product) => sum + lineValuePaise(product),
    0
  );

  const child1ShippingPaise = parentShippingPaise;
  const child2ShippingPaise = 0;

  const children = [];
  let refundAmountPaise = 0;

  if (action === 'split') {
    // Whole coupon/discount goes to the child with the higher subtotal. If it exceeds that
    // child's own subtotal, the remainder spills to the other child. Remainder of parent.total
    // always lands on child 1 so reconciliation holds.
    let child1DiscountPaise;
    let child2DiscountPaise;
    if (availSubtotalPaise >= unavailSubtotalPaise) {
      child1DiscountPaise = Math.min(parentDiscountPaise, availSubtotalPaise);
      child2DiscountPaise = parentDiscountPaise - child1DiscountPaise;
    } else {
      child2DiscountPaise = Math.min(parentDiscountPaise, unavailSubtotalPaise);
      child1DiscountPaise = parentDiscountPaise - child2DiscountPaise;
    }

    const child2TotalPaise = unavailSubtotalPaise - child2DiscountPaise + child2ShippingPaise;
    const child1TotalPaise = parentTotalPaise - child2TotalPaise;

    children.push({
      subtotal: fromPaise(availSubtotalPaise),
      discount: fromPaise(child1DiscountPaise),
      shippingCost: fromPaise(child1ShippingPaise),
      total: fromPaise(child1TotalPaise),
    });
    children.push({
      subtotal: fromPaise(unavailSubtotalPaise),
      discount: fromPaise(child2DiscountPaise),
      shippingCost: fromPaise(child2ShippingPaise),
      total: fromPaise(child2TotalPaise),
    });
  } else {
    let unavailDiscountPaise = 0;
    if (orderSubtotalPaise > 0 && unavailSubtotalPaise > 0) {
      unavailDiscountPaise = Math.floor(
        (parentDiscountPaise * unavailSubtotalPaise) / orderSubtotalPaise
      );
    }
    const availDiscountPaise = parentDiscountPaise - unavailDiscountPaise;

    let child1TotalPaise = availSubtotalPaise - availDiscountPaise + child1ShippingPaise;
    if (child1TotalPaise < 0) child1TotalPaise = 0;

    refundAmountPaise = parentTotalPaise - child1TotalPaise;
    if (refundAmountPaise < 0) {
      child1TotalPaise = parentTotalPaise;
      refundAmountPaise = 0;
    }
    children.push({
      subtotal: fromPaise(availSubtotalPaise),
      discount: fromPaise(availDiscountPaise),
      shippingCost: fromPaise(child1ShippingPaise),
      total: fromPaise(child1TotalPaise),
    });
  }

  const childrenTotalPaise = children.reduce((sum, child) => sum + toPaise(child.total), 0);
  if (childrenTotalPaise + refundAmountPaise !== parentTotalPaise) {
    throw new Error(
      `sum(children.total) + refundAmount !== parent.total ` +
        `(${childrenTotalPaise} + ${refundAmountPaise} !== ${parentTotalPaise} paise)`
    );
  }

  return {
    children,
    refundAmount: fromPaise(refundAmountPaise),
    netTotal: fromPaise(parentTotalPaise - refundAmountPaise),
  };
}

function previewPartialDelivery(parent, unavailableItems, action) {
  const { availableProducts, unavailableProducts } = partitionProducts(
    parent?.products || [],
    unavailableItems
  );
  if (availableProducts.length === 0 || unavailableProducts.length === 0) {
    return {
      children: [],
      refundAmount: 0,
      netTotal: fromPaise(toPaise(parent?.total)),
      availableProducts,
      unavailableProducts,
      canSubmit: false,
    };
  }
  const money = prorateMoney(parent, availableProducts, unavailableProducts, action);
  return {
    ...money,
    availableProducts,
    unavailableProducts,
    canSubmit: true,
  };
}

function isCodPayment(order) {
  const method = String(order?.paymentMethod || order?.payment_method || '').toLowerCase();
  return method.includes('cash_on_delivery') || method.includes('cod') || method === 'cash';
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** Parent YYYY-MM-DD + 1 calendar day (local). Mirrors the server helper. */
function addCalendarDay(dateStr) {
  if (dateStr == null || dateStr === '') return null;
  const match = String(dateStr).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  date.setDate(date.getDate() + 1);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

const UNAVAILABLE_REASONS = [
  { value: 'out_of_stock', label: 'Out of stock' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'quality_issue', label: 'Quality issue' },
  { value: 'other', label: 'Other' },
];

export {
  toPaise,
  fromPaise,
  partitionProducts,
  prorateMoney,
  previewPartialDelivery,
  isCodPayment,
  addCalendarDay,
  UNAVAILABLE_REASONS,
};
